import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaArrowLeft, FaGlobe, FaTimes } from "react-icons/fa";
import { useLanguage } from "../../components/LanguageContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Language,
  Category,
  SubCategory,
  FullPackageData,
  PackageRequest,
} from "../../Types/types";
import { addpackagetranslation } from "./addpackagetranslation";
import {
  RootState,
  AppDispatch,
} from "../../components/Manual_List/fetch/store";
import {
  fetchFullPackageById,
  fetchCategories,
  fetchSubCategories,
  postPackage,
  uploadPackageThumbnail,
  patchPackage,
  fetchThumbnail,
  deletePackageThumbnail,
} from "../../components/Manual_List/fetch/slices";
import { getSubCategoriesForCategory } from "../../components/Mappers/subcategoryToCategory";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { AddLanguageModal } from "../../components/shared/AddLanguageModal";

const AddPackage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { language } = useLanguage();
  const t = addpackagetranslation[language as Language];
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const categoryData = useSelector(
    (state: RootState) => state.data.categories.data
  ) as Category[];
  const subCategoryData = useSelector(
    (state: RootState) => state.data.subcategories.data
  ) as SubCategory[];
  const { state } = location;

  const [names, setNames] = useState<Record<string, string>>(
    state?.names || {}
  );
  const [descriptions, setDescriptions] = useState<Record<string, string>>(
    state?.descriptions || {}
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    state?.selectedCategory || ""
  );
  const [subcategories, setSubcategories] = useState<string>(
    state?.subcategories || ""
  );
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    SubCategory[]
  >([]);
  const [thumbnail, setThumbnail] = useState<File | null>(
    state?.thumbnail || null
  );
  const [dragActive, setDragActive] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [newPackageId, setNewPackageId] = useState<string | null>(null);

  const fetchPackageData = useCallback(async () => {
    if (id) {
      const result = await dispatch(fetchFullPackageById({ id, language }));
      if (result.payload) {
        const packageData = result.payload as FullPackageData;
        setNames(packageData.label);
        setDescriptions(packageData.description);
        setSelectedCategory(packageData.subCategory.categoryId.toString());
        setSubcategories(packageData.subCategory.id.toString());

        const resultThumbnail = await dispatch(fetchThumbnail({ id }));
        if (fetchThumbnail.fulfilled.match(resultThumbnail)) {
          let { thumbnailUrl } = resultThumbnail.payload;

          if (thumbnailUrl.startsWith("data:image/png;base64,")) {
            thumbnailUrl = thumbnailUrl.replace("data:image/png;base64,", "");
          }

          setThumbnailUrl(thumbnailUrl);
        }
      }
    }
  }, [id, dispatch]);

  useEffect(() => {
    fetchPackageData();
    dispatch(fetchCategories(language) as any);
    dispatch(fetchSubCategories(language) as any);
  }, [id, dispatch, language, fetchPackageData]);

  useEffect(() => {
    if (selectedCategory) {
      const categoryName = categoryData.find(
        (category) => category.id.toString() === selectedCategory
      )?.name;

      if (categoryName) {
        const filtered = getSubCategoriesForCategory(
          subCategoryData,
          categoryName
        );
        setFilteredSubCategories(filtered);
      }
    } else {
      setFilteredSubCategories([]);
    }
  }, [selectedCategory, subCategoryData, categoryData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setThumbnail(e.target.files[0]);
    }
  };

  const handleRemoveThumbnail = async () => {
    setThumbnail(null);
    setThumbnailUrl(null);
    await dispatch(deletePackageThumbnail(Number(id)));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setThumbnail(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const packageData: PackageRequest = {
      subCategoryId: parseInt(subcategories),
      label: names,
      description: descriptions,
    };

    let packageId: string | undefined = id;

    if (id) {
      const resultAction = await dispatch(patchPackage({ id, packageData }));

      if (patchPackage.fulfilled.match(resultAction)) {
        packageId = id;
        toast.success(t.packageEditedSuccess);
      }
    } else {
      const resultAction = await dispatch(postPackage(packageData));

      if (postPackage.fulfilled.match(resultAction)) {
        const newPackage = resultAction.payload;
        packageId = newPackage.id?.toString();
        if (packageId) {
          toast.success(t.packageSuccess);
          setNewPackageId(packageId);
        }
      }
    }

    if (thumbnail && packageId) {
      await dispatch(
        uploadPackageThumbnail({ id: parseInt(packageId), thumbnail })
      );
    }
  };

  const handleNavigateToSteps = () => {
    const effectiveId = id || newPackageId;

    if (effectiveId) {
      dispatch(fetchFullPackageById({ id: effectiveId, language })).then(
        (result) => {
          if (result.payload) {
            const packageData = result.payload as FullPackageData;

            navigate(`/steps-list/${effectiveId}`, {
              state: {
                id: effectiveId,
                names,
                descriptions,
                selectedCategory,
                subcategories,
                thumbnail,
                steps: packageData.packageContents,
              },
            });
          }
        }
      );
    } else {
      navigate("/steps-list", {
        state: {
          names,
          descriptions,
          selectedCategory,
          subcategories,
          thumbnail,
        },
      });
    }
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <ToastContainer />
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        <form
          id="add-package-form"
          className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-9xl flex flex-col relative"
          onSubmit={handleSubmit}
        >
          <div className="flex items-center mb-2">
            <FaArrowLeft
              className="text-gray-600 cursor-pointer mr-2"
              onClick={() => navigate("/packages-list/")}
            />
            <h1 className="text-xl font-semibold">{t.title1}</h1>
          </div>
          <div className="mb-4 flex justify-start space-x-2">
            <button
              type="button"
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
            >
              {t.details}
            </button>
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded font-medium shadow-sm dark:bg-white dark:text-black"
              onClick={handleNavigateToSteps}
            >
              {t.steps}
            </button>
          </div>
          <div className="mb-2 relative">
            <label htmlFor="name" className="block mb-1 text-base ml-2">
              {t.name}
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:bg-darkBackground dark:border-purple"
                value={names[language] || ""}
                onChange={(e) =>
                  setNames({ ...names, [language]: e.target.value })
                }
                onClick={() => setIsNameModalOpen(true)}
                autoComplete="off"
              />
              <FaGlobe className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <div className="mb-2 relative">
            <label htmlFor="description" className="block mb-1 text-base ml-2">
              {t.description}
            </label>
            <div className="relative">
              <input
                id="description"
                className="w-full pl-3 pr-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:bg-darkBackground dark:border-purple"
                value={descriptions[language] || ""}
                onChange={(e) =>
                  setDescriptions({
                    ...descriptions,
                    [language]: e.target.value,
                  })
                }
                onClick={() => setIsDescriptionModalOpen(true)}
                autoComplete="off"
              />
              <FaGlobe className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          <div className="mb-2">
            <label htmlFor="category" className="block mb-1 ml-2 text-base">
              {t.category}
            </label>
            <select
              id="category"
              className="w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:bg-darkBackground dark:border-purple"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSubcategories("");
              }}
            >
              <option value="" disabled></option>
              {Array.isArray(categoryData) &&
                categoryData.map((category: Category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="mb-4">
            <label htmlFor="subcategory" className="block mb-1 text-base ml-2">
              {t.subcategory}
            </label>
            <select
              id="subcategory"
              className="w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-0 dark:bg-darkBackground dark:border-purple"
              value={subcategories}
              onChange={(e) => setSubcategories(e.target.value)}
            >
              <option value="" disabled></option>
              {Array.isArray(filteredSubCategories) &&
                filteredSubCategories.map((subCategory: SubCategory) => (
                  <option
                    key={subCategory.id}
                    value={subCategory.id.toString()}
                  >
                    {subCategory.name}
                  </option>
                ))}
            </select>
          </div>
          <div className="mb-4 relative">
            <label htmlFor="thumbnail" className="block mb-1 text-base ml-2">
              {t.thumbnail}
            </label>
            <div
              className={`w-full p-6 border border-dashed rounded-md shadow-sm dark:bg-darkBackground dark:border-purple ${
                dragActive ? "border-purple" : "border-gray-300"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                id="thumbnail"
                className="hidden"
                onChange={handleFileChange}
              />
              <label
                htmlFor="thumbnail"
                className="w-full h-full flex justify-center items-center cursor-pointer relative"
              >
                {thumbnailUrl ? (
                  <div className="relative">
                    <img
                      src={thumbnailUrl}
                      alt="Thumbnail"
                      className="max-h-40"
                    />
                  </div>
                ) : thumbnail ? (
                  <div className="relative">
                    <p className="text-center">{thumbnail.name}</p>
                  </div>
                ) : (
                  <p className="text-center">{t.thumbnailDrop}</p>
                )}
                {(thumbnailUrl || thumbnail) && (
                  <button
                    type="button"
                    className="absolute right-0 mt-2 mr-2"
                    onClick={handleRemoveThumbnail}
                  >
                    <FaTimes className="text-red-500" />
                  </button>
                )}
              </label>
            </div>
          </div>

          <div className="absolute bottom-0 -mb-4 right-8 flex space-x-2">
            <button
              type="button"
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded font-medium shadow-sm dark:bg-white dark:text-black"
              onClick={() => navigate(-1)}
            >
              {t.discard}
            </button>
            <button
              type="submit"
              className="bg-purple text-white px-4 py-2 rounded font-medium shadow-sm"
              form="add-package-form"
            >
              {t.save}
            </button>
          </div>
        </form>
      </div>
      <AddLanguageModal
        isOpen={isNameModalOpen}
        onClose={() => setIsNameModalOpen(false)}
        translations={t}
        mode="name"
        values={names}
        setValues={setNames}
      />
      <AddLanguageModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        translations={t}
        mode="description"
        values={descriptions}
        setValues={setDescriptions}
      />
    </div>
  );
};

export default AddPackage;
